import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { ShieldCheck, CalendarCheck, ClipboardCheck, HardHat, Hammer, GraduationCap, Users, Check } from 'lucide-react';

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export default function About() {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const whoWeAreRef = useRef(null);
  const natureBreakRef = useRef(null);
  const missionValuesRef = useRef(null);
  const whyChooseUsRef = useRef(null);
  const certificationsRef = useRef(null);
  const discoverRef = useRef(null);

  const [selectedCert, setSelectedCert] = useState(null);
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const certifications = [
    { img: "2.PNG", size: "h-32" },
    { img: "3.PNG", size: "h-20" },
    { img: "4.PNG", size: "h-28" },
    { img: "1.PNG", size: "h-36" },
  ];

  // GSAP scroll-trigger animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      const animateSection = (ref) => {
        gsap.fromTo(
          ref.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: ref.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          }
        );
      };

      [heroRef, whoWeAreRef, natureBreakRef, missionValuesRef, whyChooseUsRef, certificationsRef, discoverRef].forEach(animateSection);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Show/hide scroll to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close modal on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setSelectedCert(null);
        setShowChatWidget(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Scroll to section function
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div ref={sectionRef} className="bg-white text-gray-900 font-sans overflow-x-hidden scroll-smooth relative">
      <Navbar scrollToSection={scrollToSection} refs={{ certificationsRef, discoverRef }} />

      {/* 🌿 Hero Section */}
      <section ref={heroRef} className="relative h-96 bg-gradient-to-r from-green-600 to-green-800 flex items-center justify-center text-white overflow-hidden mt-24">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: "url('./aboutpage.avif')",
          }}
        >
          <div className="absolute inset-0 bg-green-900/70"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6 max-w-4xl"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
            About <span className="text-green-100">Greenmark</span>
          </h1>
        </motion.div>
      </section>

      {/* 🌱 Who We Are */}
      <section ref={whoWeAreRef} className="py-20 px-6 md:px-20 text-center bg-gradient-to-b from-white to-green-50">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-bold text-[#0b9949] mb-6"
        >
          Who We Are
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto text-gray-700 leading-relaxed text-lg"
        >
          <span className="font-semibold text-[#0b9949]">Greenmark</span> was first incorporated and established in 2007 as a General Contractor for Building & Civil Construction, including major upgrading works and Process/Industrial Plant Engineering Design & Consultancy Services.
          <br />
          <br />
          Over the years, we have expanded our expertise to become a holistic service provider. Beyond being a leading ITE-approved training center for courses like Electrical Fitting, Metal Scaffolding, and Rigging, we now offer a full spectrum of industrial solutions ranging from manpower outsourcing to safety consulting.
        </motion.p>
      </section>


      {/* 💧 Nature / Water Break Section */}
      <section ref={natureBreakRef} className="relative h-[45vh] flex items-center justify-center overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-center bg-cover bg-fixed"
          style={{
            backgroundImage:
              "url('./aboutpagewater.avif')",
          }}
          initial={{ scale: 1.1 }}
          whileInView={{ scale: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-[#0b9949]/60" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center px-6"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
            Learning that Flows Like Water
          </h2>
          <p className="text-lg text-gray-100 mt-4 max-w-2xl mx-auto">
            Just as water shapes the world, our hands-on courses shape skilled professionals ready to excel in real industry environments.
          </p>
        </motion.div>
      </section>


      {/* 🎯 Our Mission & Values */}
      <section
        ref={missionValuesRef}
        className="py-20 px-6 md:px-20 bg-gradient-to-b from-white to-green-50 text-center"
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-bold text-[#0b9949] mb-6"
        >
          Our Mission & Values
        </motion.h2>

        <div className="mx-auto h-1 w-20 bg-[#0b9949] rounded-full mb-12"></div>

        <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto items-start">
          {/* 🌍 Mission */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-md hover:shadow-lg transition p-8 flex flex-col h-full"
          >
            <img
              src="./mission.avif"
              alt="Our Mission"
              loading="lazy"
              className="rounded-3xl h-52 w-full object-cover mb-6"
            />

            <h3 className="text-2xl font-semibold text-[#0b9949] mb-4">
              Our Mission
            </h3>

            <p className="text-gray-600 leading-relaxed max-w-md mx-auto">
              Our mission is to develop a highly skilled and safety-conscious workforce
              through accredited, competency-based training in key industrial trades such as
              Metal Scaffolding, Welding, Electrical Fitting, and Rigging & Signalling.
              <br /><br />
              We are committed to delivering quality education, hands-on practical
              experience, and industry-aligned assessments that prepare learners to meet
              Singapore’s safety, productivity, and professional standards. We empower
              individuals and organisations to perform with confidence, competence, and
              excellence in today’s evolving industrial environment.
            </p>
          </motion.div>

          {/* 💚 Values */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-md hover:shadow-lg transition p-8 flex flex-col h-full"
          >
            <img
              src="./values.avif"
              alt="Our Values"
              loading="lazy"
              className="rounded-3xl h-52 w-full object-cover mb-6"
            />

            <h3 className="text-2xl font-semibold text-[#0b9949] mb-4">
              Our Values
            </h3>

            <div className="text-gray-600 leading-relaxed max-w-md mx-auto space-y-4 text-left">
              <p><strong>Integrity</strong> — We conduct all training and assessments with honesty, fairness, and transparency.</p>
              <p><strong>Excellence</strong> — We deliver high standards in teaching, safety, and service quality.</p>
              <p><strong>Innovation</strong> — We continuously enhance our programs to meet evolving industry standards.</p>
              <p><strong>Safety First</strong> — We provide a secure learning environment and promote safe work practices.</p>
              <p><strong>Commitment to Learners</strong> — We support each learner’s growth and long-term success.</p>
              <p><strong>Professional Responsibility</strong> — We contribute to Singapore’s workforce by producing skilled and disciplined professionals.</p>
            </div>
          </motion.div>
        </div>
      </section>


      {/* 🏗️ Our Core Capabilities */}
      <section className="py-20 px-6 md:px-20 bg-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#0b9949] mb-4">
            Our Core Capabilities
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-16 text-lg">
            Delivering excellence across Safety, Engineering, Manpower, and Training.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 text-left max-w-7xl mx-auto">
          {/* Outsourcing - Professional */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">WSH Outsourcing</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> WSH Officer / Manager</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> WSH Coordinator / Supervisor</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Environmental Control Officer (ECO)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Fire Safety Manager (FSM)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Lifting / Scaffold Supervisor</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Confined Space Assessor</li>
            </ul>
          </motion.div>

          {/* Consulting */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
              <CalendarCheck className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Consulting & Standards</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> bizSAFE Level 3 to Star</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Risk Management / GGBS</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> ISO 9001, 14001, 45001</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Food Safety & ISO 22000</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Business Continuity Mgmt (BCM)</li>
            </ul>
          </motion.div>

          {/* Auditing */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
              <ClipboardCheck className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Auditing Services</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> ConSASS Audit</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> SHMS Audit</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Risk Management (bizSAFE) Audit</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> OT Exemption / Sharp Award Audit</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> NEA Hazardous Substances Audit</li>
            </ul>
          </motion.div>

          {/* Outsourcing - Technical */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
              <HardHat className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Technical Manpower</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Professional Engineer (PE)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Resident Engineer (RE)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Resident Tech Officer (RTO)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> QC / Site Engineer / Supervisor</li>
            </ul>
          </motion.div>

          {/* Project Execution */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
              <Hammer className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Civil & Minor Projects</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Cable Laying & Excavation</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Turfing & Interlock Tile Work</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Mixed Construction Work</li>
            </ul>
          </motion.div>

          {/* Training (Brief Recapping) */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6 text-green-600">
              <GraduationCap className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Specialized Training</h3>
            <ul className="space-y-3 text-gray-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Professional & Manager Courses</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Supervisor & Worker Courses</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Corporate Application Training</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" /> Customized In-house Training</li>
            </ul>
          </motion.div>

          {/* Outsourcing - Skilled Workers */}
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-green-50/50 p-8 rounded-2xl border border-green-100 hover:shadow-xl transition-all duration-300 md:col-span-2 lg:col-span-3"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                <Users className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Skilled Workforce Supply</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 text-sm text-gray-600">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> General Worker</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Rigger / Signalman</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Electrician</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Forklift Supervisor</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Carpenter / Formwork</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Rebar Worker</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Brick Layer / Plasterer</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Welder (3G, 4G, 6G)</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Excavator Operator</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Traffic Controller</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Scaffold Erector</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Tunnel Worker</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Boom / Scissor Lift Op.</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Steel Fitter</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> General Foreman</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 text-sm flex-shrink-0" /> Banksman</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 🌟 Why Choose Us */}
      <section
        ref={whyChooseUsRef}
        className="py-20 px-6 md:px-20 bg-[#f6fdf9] text-center"
      >
        <h2 className="text-3xl font-bold text-[#0b9949] mb-4">
          Why Choose Greenmark
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-12 text-lg">
          We provide professional, high-quality training programs designed to
          enhance skills, boost confidence, and support career development across
          multiple industries.
        </p>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "Accredited & Industry-Recognized",
              desc: "Our training programs follow well-established industry standards and ensure strong professional credibility.",
            },
            {
              title: "Experienced Trainers",
              desc: "All instructors bring years of practical expertise, offering clear guidance and real-world knowledge.",
            },
            {
              title: "Hands-On Practical Learning",
              desc: "Learners engage in interactive sessions, demonstrations, and practical activities to build competence.",
            },
            {
              title: "Wide Range of Courses",
              desc: "Our curriculum covers multiple fields, allowing individuals and teams to choose programs that fit their goals.",
            },
            {
              title: "Modern Training Facilities",
              desc: "Training centers are equipped with updated tools, structured classrooms, and a learner-friendly environment.",
            },
            {
              title: "Focused on Skill Development",
              desc: "We emphasize strong foundational understanding, clear learning outcomes, and measurable skill growth.",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-white p-8 rounded-xl shadow-md hover:shadow-2xl
                   transition-all duration-500 hover:-translate-y-2 
                   hover:scale-105"
            >
              <h4 className="text-xl font-semibold text-[#0b9949] mb-3">
                {item.title}
              </h4>
              <p className="text-gray-600 text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>


      {/* 🏅 Our Certifications */}
      <section ref={certificationsRef} className="py-20 px-6 md:px-20 bg-white text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-[#0b9949] mb-16">
          Our Certifications
        </h2>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="flex flex-wrap justify-center items-center gap-6 md:gap-8 max-w-7xl mx-auto"
        >
          {certifications.map((cert, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -10, scale: 1.2, rotate: 3 }}
              className="bg-white p-6 md:p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center justify-center border border-gray-100 cursor-pointer"
              onClick={() => setSelectedCert(cert.img)}
            >
              <img
                src={cert.img}
                alt="certification logo"
                className={`${cert.size} w-auto object-contain`}
                loading="lazy"
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Modal */}
        {selectedCert && (
          <div
            className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
            onClick={() => setSelectedCert(null)}
          >
            <motion.img
              src={selectedCert}
              alt="Certification enlarged"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="max-h-[90%] max-w-[90%] object-contain rounded-lg shadow-2xl"
            />
          </div>
        )}
      </section>

      {/* 🌍 Discover Section */}
      <section ref={discoverRef} className="relative text-center text-white py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url('./aboutpagediscover.avif')",
          }}
        />
        <div className="absolute inset-0 bg-[#0b9949]/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-6">
            Discover Greenmark Practices for a Sustainable Future
          </h2>
          <p className="text-lg text-gray-100 leading-relaxed mb-6">
            Our programs go beyond compliance — they inspire innovation,
            accountability, and environmental consciousness.
          </p>
          <motion.button
            whileHover={{ scale: 1.1 }}
            className="bg-white text-[#0b9949] px-8 py-3 rounded-full font-medium hover:bg-green-100 transition"
            onClick={() => scrollToSection(certificationsRef)}
          >
            Contact Us
          </motion.button>
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-24 right-6 bg-[#0b9949] text-white p-4 rounded-full shadow-2xl hover:bg-[#087a38] transition-all z-50 hover:scale-110"
          title="Scroll to top"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </motion.button>
      )}

      {/* WhatsApp Chat Widget Button */}
      {!showChatWidget && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1, type: "spring" }}
          onClick={() => setShowChatWidget(true)}
          className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:bg-[#20BA5A] transition-all z-50 hover:scale-110"
        >
          <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </motion.button>
      )}

      {/* Chat Widget */}
      {showChatWidget && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          className="fixed bottom-6 right-6 w-[90vw] max-w-sm shadow-2xl rounded-2xl overflow-hidden z-50"
        >
          {/* Header */}
          <div className="bg-[#25D366] px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-3">
              {/* Avatar Circle with Logo */}
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-md overflow-hidden">
                <img src="./logo.png" alt="Greenmark Logo" className="w-7 h-7 object-cover" />
              </div>
              {/* Company Name & Badge */}
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-white font-semibold text-base">Greenmark</span>
                  {/* Verification Tick */}
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-white text-xs font-light opacity-90">
                  Active
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowChatWidget(false)}
              className="w-8 h-8 rounded-full bg-black bg-opacity-20 flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
            >
              <span className="text-xl font-bold">×</span>
            </button>
          </div>

          {/* Chat Body */}
          <div className="bg-white p-6 h-64 flex flex-col justify-between">
            {/* Message Bubble */}
            <div className="relative max-w-xs">
              <div className="bg-gray-100 text-gray-800 rounded-xl rounded-bl-sm p-4 shadow-sm">
                <p className="font-medium">Hello</p>
                <p className="text-sm">Can we help you?</p>
              </div>
              <div className="absolute bottom-0 left-0 -ml-1 w-4 h-4 transform rotate-45 bg-gray-100" style={{ clipPath: "polygon(0 0, 100% 100%, 0 100%)" }}></div>
            </div>

            {/* Open Chat Button */}
            <div className="flex justify-end">
              <a
               href="https://wa.me/6580147799?text=Hi%20Greenmark"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white font-medium py-3 px-6 rounded-full flex items-center gap-2 shadow-md hover:bg-[#20BA5A] transition-all hover:scale-105"
              >
                <span>Open chat</span>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
              </a>
            </div>
          </div>
        </motion.div>
      )}

      <Footer />
    </div>
  );
}
